import type { StreamStatus } from "../../../../../stream";
import type { ScoutActiveTurn, ScoutTurnStatus } from "../../hooks/types";
import type { ScoutChatMessage } from "./scoutChatMessage";

export const SCOUT_ASSISTANT_MESSAGE_ID_PREFIX = "scout-assistant-";

// Default friendly copy shown for any failed Scout turn that isn't covered
// by a specific code below. The backend writes a single canonical
// terminal-error code to every channel (DB, Valkey stream, terminal ack),
// so a failed turn reads identically whether it just happened (live in
// ChatChrome) or was reloaded from persisted history (AgentMessage's
// terminal-error render, ENG-15662).
export const TURN_ERROR_MESSAGE =
  "So sorry, I ran into a problem on my end and couldn't finish the response. I apologize for the inconvenience.";
// Appended only when a live retry affordance is shown alongside the apology.
// On a reload there's no retained turn to retry, so the suffix is omitted.
const TURN_ERROR_RETRY_SUFFIX = " Would you like to try again?";

/**
 * Per-code customer copy. Keys are terminal-error codes emitted by the
 * Scout runtime and allowlisted by the materializer
 * (CLIENT_SAFE_TERMINAL_ERROR_CODES). Any code not in this map falls back
 * to {@link TURN_ERROR_MESSAGE}.
 *
 * Backend constants for the keys live next to the code that emits them
 * (e.g. SCOUT_COST_LIMIT_EXCEEDED_TERMINAL_ERROR_CODE in
 * packages/encamp-scout-connection/src/cost-limits.ts). The strings are
 * duplicated here on purpose — encamp-scout-connection is a backend
 * workspace and customer-facing UI strings are owned by the Oak app.
 */
const TURN_ERROR_MESSAGE_BY_CODE: Record<string, string> = {
  scoutCostLimitExceeded:
    "Scout is unavailable because this month's usage limit has been reached. Please reach out to customer success to continue.",
};

/**
 * Terminal-error codes for which retry doesn't make sense (the same
 * condition will fire again). The retry button + retry suffix are
 * suppressed for these even when the caller would otherwise allow it.
 */
const NON_RETRIABLE_TERMINAL_ERROR_CODES = new Set<string>(["scoutCostLimitExceeded"]);

export function getTurnErrorMessage(props?: { canRetry?: boolean; code?: string }): string {
  const codedMessage = props?.code ? TURN_ERROR_MESSAGE_BY_CODE[props.code] : undefined;
  const baseMessage = codedMessage ?? TURN_ERROR_MESSAGE;
  const effectiveCanRetry = props?.canRetry && !isNonRetriableTerminalErrorCode(props?.code);
  return effectiveCanRetry ? `${baseMessage}${TURN_ERROR_RETRY_SUFFIX}` : baseMessage;
}

export function isNonRetriableTerminalErrorCode(code: string | undefined): boolean {
  return !!code && NON_RETRIABLE_TERMINAL_ERROR_CODES.has(code);
}

/** Turn statuses where the current assistant message UI should stay suppressed. */
export const AGENT_TURN_IN_PROGRESS_STATUSES = new Set<ScoutTurnStatus>([
  "starting",
  "streaming",
  "error",
  "aborted",
]);

export function getScoutAssistantMessageId(requestId: string): string {
  return `${SCOUT_ASSISTANT_MESSAGE_ID_PREFIX}${requestId}`;
}

export function isCurrentActiveAssistantMessage(
  message: ScoutChatMessage,
  activeTurn: ScoutActiveTurn
): boolean {
  return message.id === getScoutAssistantMessageId(activeTurn.requestId);
}

export function shouldSuppressAgentMessageToolbar(input: {
  message: ScoutChatMessage;
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
}): boolean {
  if (input.message.isStreaming) {
    return true;
  }

  if (input.activeTurn && isCurrentActiveAssistantMessage(input.message, input.activeTurn)) {
    if (AGENT_TURN_IN_PROGRESS_STATUSES.has(input.activeTurn.status)) {
      return true;
    }

    if (input.streamStatus === "error") {
      return true;
    }
  }

  return false;
}

/** True when the agent turn is finished enough to show post-turn UI (opt-in, etc.). */
export function isCurrentAgentTurnComplete(input: {
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
  messages: ScoutChatMessage[];
}): boolean {
  if (input.messages.some((message) => message.role === "assistant" && message.isStreaming)) {
    return false;
  }

  if (!input.activeTurn) {
    return true;
  }

  if (AGENT_TURN_IN_PROGRESS_STATUSES.has(input.activeTurn.status)) {
    return false;
  }

  if (input.streamStatus === "error") {
    return false;
  }

  return true;
}
